import {
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ClassificationRuleApi } from "../../apis"
import type {
  IClassificationRule,
  ICreateClassificationRuleInput,
} from "../../apis/classification-rule"
import useSWR from "swr"
import { SizeScaleApi } from "../../apis"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsActionCell, SettingsButton, SettingsRowAction } from "./settings-controls"
import { CustomTable, ITableRef } from "../table/customTable"
import RenderText from "../ui/render-text"
import { BooleanIndicator } from "./atoms"
import { useSettingsMutations } from "./hooks"
import { SettingsModal } from "./settings-modal"

function ClassificationRuleFormModal({
  opened,
  onClose,
  initial,
  onSaved,
}: {
  opened: boolean
  onClose: () => void
  initial?: IClassificationRule | null
  onSaved?: () => void
}) {
  const { t } = useTranslation("settings")
  const mutations = useSettingsMutations()
  const isEdit = !!initial

  const channelOptions = [
    { value: "LOCAL_DELIVERY",  label: t("classification.options.channel.LOCAL_DELIVERY") },
    { value: "SUPPLY_FREIGHT",  label: t("classification.options.channel.SUPPLY_FREIGHT") },
    { value: "CUSTOMER_PICKUP", label: t("classification.options.channel.CUSTOMER_PICKUP") },
  ]

  const aggregationOptions = [
    { value: "SUM",      label: t("classification.options.aggregation.SUM") },
    { value: "MAX",      label: t("classification.options.aggregation.MAX") },
    { value: "WEIGHTED", label: t("classification.options.aggregation.WEIGHTED") },
  ]

  const form = useForm<ICreateClassificationRuleInput>({
    initialValues: {
      sizeScaleId: initial?.sizeScaleId ?? "",
      name: initial?.name ?? "",
      smallThreshold: initial ? parseFloat(initial.smallThreshold) : 0,
      largeThreshold: initial ? parseFloat(initial.largeThreshold) : 0,
      weightThreshold: initial?.weightThreshold ? parseFloat(initial.weightThreshold) : null,
      smallChannel: initial?.smallChannel ?? null,
      largeChannel: initial?.largeChannel ?? null,
      mediumChannel: initial?.mediumChannel ?? null,
      sizeAggregation: (initial?.sizeAggregation as any) ?? "SUM",
      packingEfficiency: initial ? parseFloat(initial.packingEfficiency) : 1,
      isActive: initial?.isActive ?? true,
      priority: initial?.priority ?? 0,
    },
    validate: {
      sizeScaleId: (v) => (!v.trim() ? t("classification.validation.sizeScaleIdRequired") : null),
      name: (v) => (!v.trim() ? t("classification.validation.nameRequired") : null),
      smallThreshold: (v, vals) => {
        const small = typeof v === "number" ? v : parseFloat(v as any)
        const large = typeof vals.largeThreshold === "number" ? vals.largeThreshold : parseFloat(vals.largeThreshold as any)
        return !isNaN(small) && !isNaN(large) && small >= large
          ? t("classification.validation.smallThresholdRange")
          : null
      },
      largeThreshold: (v, vals) => {
        const large = typeof v === "number" ? v : parseFloat(v as any)
        const small = typeof vals.smallThreshold === "number" ? vals.smallThreshold : parseFloat(vals.smallThreshold as any)
        return !isNaN(large) && !isNaN(small) && large <= small
          ? t("classification.validation.largeThresholdRange")
          : null
      },
      packingEfficiency: (v) => {
        const n = typeof v === "number" ? v : parseFloat(v as any)
        return isNaN(n) || n < 0.1 || n > 1 ? t("classification.validation.packingEfficiencyRange") : null
      },
    },
  })

  const handleSubmit = async (values: ICreateClassificationRuleInput) => {
    const wt = values.weightThreshold as number | string | null | undefined
    const payload: ICreateClassificationRuleInput = {
      ...values,
      weightThreshold:
        wt === null || wt === undefined || wt === "" || isNaN(Number(wt))
          ? null
          : Number(wt),
    }
    if (isEdit && initial) {
      await mutations.classificationRule.update(initial.id, payload)
    } else {
      await mutations.classificationRule.create(payload)
    }
    onSaved?.()
    onClose()
    form.reset()
  }

  return (
    <SettingsModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("classification.modal.editTitle") : t("classification.modal.createTitle")}
      onSubmit={form.onSubmit(handleSubmit)}
      submitLabel={isEdit ? t("common.save") : t("common.create")}
      submitting={mutations.submitting}
    >
          <TextInput
            label={t("classification.fields.sizeScaleId.label")}
            description={t("classification.fields.sizeScaleId.description")}
            placeholder={t("classification.fields.sizeScaleId.placeholder")}
            required
            {...form.getInputProps("sizeScaleId")}
          />
          <TextInput
            label={t("classification.fields.name.label")}
            required
            {...form.getInputProps("name")}
          />
          <Group grow>
            <NumberInput
              label={t("classification.fields.smallThreshold.label")}
              description={t("classification.fields.smallThreshold.description")}
              decimalScale={2}
              required
              {...form.getInputProps("smallThreshold")}
            />
            <NumberInput
              label={t("classification.fields.largeThreshold.label")}
              description={t("classification.fields.largeThreshold.description")}
              decimalScale={2}
              required
              {...form.getInputProps("largeThreshold")}
            />
          </Group>
          <NumberInput
            label={t("classification.fields.weightThreshold.label")}
            description={t("classification.fields.weightThreshold.description")}
            decimalScale={2}
            min={0}
            {...form.getInputProps("weightThreshold")}
          />
          <Group grow>
            <Select
              label={t("classification.fields.smallChannel.label")}
              description={t("classification.fields.smallChannel.description")}
              data={channelOptions}
              clearable
              {...form.getInputProps("smallChannel")}
            />
            <Select
              label={t("classification.fields.mediumChannel.label")}
              data={channelOptions}
              clearable
              {...form.getInputProps("mediumChannel")}
            />
            <Select
              label={t("classification.fields.largeChannel.label")}
              data={channelOptions}
              clearable
              {...form.getInputProps("largeChannel")}
            />
          </Group>
          <Group grow>
            <Select
              label={t("classification.fields.sizeAggregation.label")}
              description={t("classification.fields.sizeAggregation.description")}
              data={aggregationOptions}
              {...form.getInputProps("sizeAggregation")}
            />
            <NumberInput
              label={t("classification.fields.packingEfficiency.label")}
              description={t("classification.fields.packingEfficiency.description")}
              decimalScale={2}
              step={0.01}
              min={0.1}
              max={1}
              {...form.getInputProps("packingEfficiency")}
            />
          </Group>
          <NumberInput
            label={t("classification.fields.priority.label")}
            min={0}
            {...form.getInputProps("priority")}
          />
          <Checkbox
            label={t("classification.fields.isActive.label")}
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
          />
    </SettingsModal>
  )
}

export function ClassificationRuleTab() {
  const { t } = useTranslation("settings")
  // The rule row carries only the scale id; show the scale's name, never the id.
  const { data: scaleData } = useSWR("settings.classification.scale-names", () => SizeScaleApi.list({ offset: { limit: 200, skip: 0 } }), { revalidateOnFocus: false })
  const scaleNames = new Map((scaleData?.rows ?? []).map((sc: { id: string; name: string }) => [sc.id, sc.name] as const))
  const [search, setSearch] = useState("")
  const [activeOnly, setActiveOnly] = useState(false)
  const [editTarget, setEditTarget] = useState<IClassificationRule | null>(null)
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const tableRef = useRef<ITableRef>(null)
  const mutations = useSettingsMutations()

  const filters = useMemo(() => {
    const next: { search?: string; isActive?: boolean } = {}
    if (search) next.search = search
    if (activeOnly) next.isActive = true
    return next
  }, [search, activeOnly])

  const handleEdit = (rule: IClassificationRule) => {
    setEditTarget(rule)
    openEdit()
  }

  const handleDelete = (rule: IClassificationRule) => {
    modals.openConfirmModal({
      title: t("common.delete"),
      children: (
        <Text size="sm">
          {t("common.confirmDelete", { name: rule.name })}
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        await mutations.classificationRule.remove(rule.id)
        tableRef.current?.reload()
      },
    })
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <TextInput
            placeholder={t("common.empty")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={220}
          />
          <Switch
            label={t("common.active")}
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.currentTarget.checked)}
          />
        </Group>
        <SettingsButton code={SETTINGS_WRITE.classification} leftSection={<PlusIcon size={18} />} onClick={openCreate}>
          {t("classification.actions.newRule")}
        </SettingsButton>
      </Group>

      <Table.ScrollContainer minWidth={800}>
        <CustomTable
          ref={tableRef}
          name="classification-rules"
          colCount={8}
          filters={filters}
          empty={<ListState state={search || activeOnly ? "noResults" : "empty"} title={t("common.empty")} description={search || activeOnly ? undefined : t("common.emptyHint")} action={search || activeOnly ? { onClick: () => { setSearch(""); setActiveOnly(false) } } : undefined} />}
          renderError={(retry) => <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={retry} />}
          loadData={async (filter) => {
            const page = filter?.offset?.page ?? 1
            const limit = filter?.offset?.limit ?? 20
            const f = (filter?.filter ?? {}) as { search?: string; isActive?: boolean }
            return ClassificationRuleApi.list({
              filter: {
                ...(f.search ? { search: f.search } : {}),
                ...(f.isActive !== undefined ? { isActive: f.isActive } : {}),
              },
              offset: { limit, skip: (page - 1) * limit },
            })
          }}
          thead={
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={120}>{t("common.actions")}</Table.Th>
                <Table.Th>{t("classification.table.name")}</Table.Th>
                <Table.Th>{t("classification.table.scaleId")}</Table.Th>
                <Table.Th>{t("classification.fields.smallThreshold.label")}</Table.Th>
                <Table.Th>{t("classification.fields.largeThreshold.label")}</Table.Th>
                <Table.Th>{t("classification.fields.weightThreshold.label")}</Table.Th>
                <Table.Th>
                  <Tooltip
                    label={t("classification.fields.packingEfficiency.description")}
                    multiline
                    w={260}
                    withArrow
                  >
                    <Text span style={{ cursor: "help", textDecoration: "underline dotted" }}>
                      {t("classification.table.packing")}
                    </Text>
                  </Tooltip>
                </Table.Th>
                <Table.Th>{t("classification.table.active")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
          }
          tbody={(rows: IClassificationRule[]) => (
            <Table.Tbody>
              {rows.map((rule) => (
                <Table.Tr key={rule.id}>
                  <Table.Td>
                    <SettingsActionCell>
                      <SettingsRowAction
                        code={SETTINGS_WRITE.classification}
                        icon={<PencilSimpleIcon />}
                        label={t("common.edit")}
                        onClick={() => handleEdit(rule)}
                      />
                      <SettingsRowAction
                        code={SETTINGS_WRITE.classification}
                        icon={<TrashIcon />}
                        label={t("common.delete")}
                        onClick={() => handleDelete(rule)}
                      />
                    </SettingsActionCell>
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={rule.name} fontWeight={600} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={scaleNames.get(rule.sizeScaleId) ?? "-"} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={rule.smallThreshold} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={rule.largeThreshold} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={rule.weightThreshold ? rule.weightThreshold : "—"} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={rule.packingEfficiency} />
                  </Table.Td>
                  <Table.Td>
                    <BooleanIndicator value={rule.isActive} trueLabel={t("common.active")} falseLabel={t("common.inactive")} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          )}
        />
      </Table.ScrollContainer>

      <ClassificationRuleFormModal
        opened={createOpen}
        onClose={closeCreate}
        onSaved={() => tableRef.current?.reload()}
      />
      <ClassificationRuleFormModal
        opened={editOpen}
        onClose={closeEdit}
        initial={editTarget}
        onSaved={() => tableRef.current?.reload()}
      />
    </Stack>
  )
}
